import { useState, useEffect } from 'react';
import { topicsAPI, departmentsAPI } from '../lib/api';
import { LoadingPage, PageHeader, Modal, Alert } from '../components/shared';
import { Plus, Lightbulb, Users, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';

const DOMAINS = ['Power Systems','Control Systems','Electronics','Communications','Computer Engineering','Biomedical Engineering','Renewable Energy','Signal Processing','Electromagnetics','Embedded Systems','IoT','Robotics','Other'];

const emptyForm = {
  title: '', description: '', domain: '', required_skills: '',
  expected_outcomes: '', department_id: '',
  team_enabled: false, application_mode: 'individual', max_team_size: '',
};

const statusColor = s => ({
  pending_approval: 'var(--warning)', open: 'var(--teal)',
  sponsored: 'var(--gold)', rejected: 'var(--error)', closed: 'var(--text-muted)'
}[s] || 'var(--text-muted)');

export default function StudentTopicsPage() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showTeamSettings, setShowTeamSettings] = useState(false);

  const load = () => {
    setLoading(true);
    topicsAPI.getMy()
      .then(r => setTopics(r.data || []))
      .catch(() => setTopics([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    departmentsAPI.getAll().then(r => setDepartments(r.data)).catch(() => {});
  }, []);

  const handleTeamToggle = (enabled) => {
    setForm(f => ({
      ...f,
      team_enabled: enabled,
      application_mode: enabled ? f.application_mode : 'individual',
      max_team_size: enabled ? f.max_team_size : '',
    }));
    if (enabled) setShowTeamSettings(true);
  };

  const handleSubmit = async () => {
    setError('');
    if (!form.title || !form.description || !form.domain) {
      setError('Title, description and domain are required.');
      return;
    }
    if (form.team_enabled && form.max_team_size && parseInt(form.max_team_size) < 2) {
      setError('Maximum team size must be at least 2.');
      return;
    }
    setSubmitting(true);
    try {
      await topicsAPI.create({
        ...form,
        required_skills: form.required_skills.split(',').map(s => s.trim()).filter(Boolean),
        student_slots: 1,
        team_enabled: form.team_enabled,
        application_mode: form.team_enabled ? form.application_mode : 'individual',
        max_team_size: form.team_enabled && form.max_team_size ? parseInt(form.max_team_size) : null,
      });
      toast.success('Topic submitted! Admin will review and publish it.');
      setShowForm(false);
      setForm(emptyForm);
      setShowTeamSettings(false);
      load();
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || 'Failed to submit topic.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingPage />;

  return (
    <div>
      <PageHeader
        eyebrow="Student"
        title="My Research Ideas"
        subtitle="Post your own research topic — alumni can discover it and offer sponsorship or mentorship"
        actions={<button className="btn btn-primary" onClick={() => setShowForm(true)}><Plus size={16} /> Post an Idea</button>}
      />

      {topics.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}>
          <Lightbulb size={40} style={{ color: 'var(--gold)', margin: '0 auto 16px', opacity: 0.5 }} />
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 400, marginBottom: 8 }}>Share your research idea</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: 24 }}>Post a topic you want to work on — alumni sponsors can discover it and fund your project.</p>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}><Plus size={16} /> Post Your First Idea</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {topics.map(topic => (
            <div key={topic.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div className="eyebrow">{topic.domain}</div>
                    {topic.team_enabled && (
                      <span style={{ fontSize: '0.72rem', color: 'var(--gold)', background: 'rgba(201,168,76,0.1)', padding: '2px 8px', borderRadius: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Users size={10} /> Team project
                      </span>
                    )}
                  </div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', fontWeight: 500, marginBottom: 8 }}>{topic.title}</h3>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 12 }}>{topic.description}</p>
                  {topic.required_skills?.length > 0 && (
                    <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {topic.required_skills.map((s, i) => (
                        <span key={i} style={{ background: 'rgba(45,212,191,0.08)', color: 'var(--teal)', border: '1px solid rgba(45,212,191,0.15)', padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', fontFamily: 'var(--font-mono)' }}>{s}</span>
                      ))}
                    </div>
                  )}
                </div>
                <span style={{ padding: '4px 12px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600, background: `${statusColor(topic.status)}22`, color: statusColor(topic.status), textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>
                  {topic.status?.replace(/_/g, ' ')}
                </span>
              </div>
              {topic.admin_feedback && (
                <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, fontSize: '0.875rem', color: 'var(--error)' }}>
                  <strong>Admin feedback:</strong> {topic.admin_feedback}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={showForm} onClose={() => { setShowForm(false); setError(''); setForm(emptyForm); setShowTeamSettings(false); }} title="Post a Research Idea" size="lg">
        {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 20, lineHeight: 1.6 }}>
          Share a research topic you're passionate about. Once approved, alumni sponsors will be able to discover and fund your project.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">Topic Title *</label>
            <input className="form-input" placeholder="e.g. Low-Cost ECG Monitor for Rural Clinics"
              value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Department *</label>
            <select className="form-select" value={form.department_id} onChange={e => setForm(f => ({ ...f, department_id: e.target.value }))}>
              <option value="">Select department…</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Domain *</label>
            <select className="form-select" value={form.domain} onChange={e => setForm(f => ({ ...f, domain: e.target.value }))}>
              <option value="">Select domain…</option>
              {DOMAINS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Description *</label>
            <textarea className="form-textarea" rows={4}
              placeholder="Describe the problem you want to solve and your proposed approach…"
              value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Expected Outcomes</label>
            <textarea className="form-textarea" rows={2} placeholder="What will you build or deliver?"
              value={form.expected_outcomes} onChange={e => setForm(f => ({ ...f, expected_outcomes: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Your Skills <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(comma-separated)</span></label>
            <input className="form-input" placeholder="e.g. Python, MATLAB, Circuit Design"
              value={form.required_skills} onChange={e => setForm(f => ({ ...f, required_skills: e.target.value }))} />
          </div>

          {/* ── Self-assignment note for student-originated topics ──────── */}
          <div style={{ padding: '12px 16px', borderRadius: 8, background: 'rgba(42,157,143,0.08)', border: '1px solid rgba(42,157,143,0.2)', fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span style={{ color: 'var(--teal)', flexShrink: 0, marginTop: 1 }}>ℹ</span>
            <span>This topic will be <strong style={{ color: 'var(--teal)' }}>self-assigned to you</strong> once a sponsor funds it. No other students can apply.</span>
          </div>

          {/* ── Team Project Settings — hidden for student-originated topics ── */}
          <div style={{ display: 'none', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>
            <button
              type="button"
              onClick={() => setShowTeamSettings(s => !s)}
              style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'rgba(255,255,255,0.02)', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '0.875rem' }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Users size={15} style={{ color: 'var(--teal)' }} />
                Team project settings
                {form.team_enabled && <span style={{ fontSize: '0.72rem', color: 'var(--gold)', background: 'rgba(201,168,76,0.1)', padding: '1px 7px', borderRadius: 4 }}>Enabled</span>}
              </span>
              {showTeamSettings ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </button>

            {showTeamSettings && (
              <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 16, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', fontWeight: 500 }}>Allow a team to work on this topic</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>Other students can join your project as a team</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleTeamToggle(!form.team_enabled)}
                    style={{ width: 44, height: 24, borderRadius: 12, flexShrink: 0, background: form.team_enabled ? 'var(--teal)' : 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}
                  >
                    <span style={{ position: 'absolute', top: 2, left: form.team_enabled ? 22 : 2, width: 20, height: 20, borderRadius: 10, background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                  </button>
                </div>

                {form.team_enabled && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Application Mode</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
                        {[
                          { value: 'pre_formed', label: 'We form our team before applying', desc: 'You and your teammates apply together as a pre-formed group.' },
                          { value: 'individual', label: 'Sponsor selects team members individually', desc: 'Students apply separately and the sponsor assembles the team.' },
                        ].map(opt => (
                          <label key={opt.value} style={{ display: 'flex', gap: 12, padding: '12px 14px', borderRadius: 8, border: `1px solid ${form.application_mode === opt.value ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.08)'}`, background: form.application_mode === opt.value ? 'rgba(201,168,76,0.05)' : 'transparent', cursor: 'pointer' }}>
                            <input type="radio" name="application_mode" value={opt.value} checked={form.application_mode === opt.value} onChange={e => setForm(f => ({ ...f, application_mode: e.target.value }))} style={{ marginTop: 2, flexShrink: 0 }} />
                            <div>
                              <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', fontWeight: 500 }}>{opt.label}</div>
                              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>{opt.desc}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Maximum Team Size <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                      <input className="form-input" type="number" min={2} placeholder="No limit"
                        value={form.max_team_size} onChange={e => setForm(f => ({ ...f, max_team_size: e.target.value }))}
                        style={{ maxWidth: 140 }} />
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Leave blank for no limit. Minimum 2.</div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
            <button className="btn btn-ghost" onClick={() => { setShowForm(false); setError(''); setForm(emptyForm); setShowTeamSettings(false); }}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>{submitting ? 'Submitting…' : 'Submit Idea'}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
